import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Responsive, WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import { FiPlus, FiSave, FiTrash2, FiCopy, FiEye, FiEyeOff, FiDatabase, FiChevronDown, FiChevronRight, FiHash, FiType, FiCalendar, FiSearch, FiX, FiSettings, FiEdit2 } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../utils/api'
import ChartWidget from '../components/charts/ChartWidget'

const ResponsiveGridLayout = WidthProvider(Responsive)

const WIDGET_TYPES = [
  { type: 'kpi', label: 'KPI Karti', icon: '📊', defaultW: 3, defaultH: 2 },
  { type: 'bar', label: 'Bar Grafik', icon: '📶', defaultW: 6, defaultH: 4 },
  { type: 'horizontal_bar', label: 'Yatay Bar', icon: '📊', defaultW: 6, defaultH: 4 },
  { type: 'line', label: 'Cizgi Grafik', icon: '📈', defaultW: 6, defaultH: 4 },
  { type: 'area', label: 'Alan Grafik', icon: '📉', defaultW: 6, defaultH: 4 },
  { type: 'pie', label: 'Pasta Grafik', icon: '🥧', defaultW: 4, defaultH: 4 },
  { type: 'donut', label: 'Donut Grafik', icon: '🍩', defaultW: 4, defaultH: 4 },
  { type: 'table', label: 'Tablo', icon: '📋', defaultW: 12, defaultH: 5 },
  { type: 'gauge', label: 'Gosterge', icon: '⏱️', defaultW: 3, defaultH: 3 },
]

const TYPE_ICONS = { number: FiHash, text: FiType, date: FiCalendar }

export default function DashboardDesigner() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [dashboard, setDashboard] = useState(null)
  const [activePage, setActivePage] = useState(0)
  const [selectedWidget, setSelectedWidget] = useState(null)
  const [showAddWidget, setShowAddWidget] = useState(false)
  const [showPageForm, setShowPageForm] = useState(false)
  const [pageName, setPageName] = useState('')
  const [layouts, setLayouts] = useState({})

  // Veri kaynaklari ve semalari
  const [datasources, setDatasources] = useState([])
  const [schemas, setSchemas] = useState({}) // dsId -> schema
  const [expandedDs, setExpandedDs] = useState({})
  const [expandedTable, setExpandedTable] = useState({})
  const [fieldSearch, setFieldSearch] = useState('')

  // Sorgular ve degiskenler
  const [queries, setQueries] = useState([])
  const [variables, setVariables] = useState([])

  // Panel sekmeleri
  const [rightTab, setRightTab] = useState('fields')
  const [leftTab, setLeftTab] = useState('vars') // vars | tables
  const [showOtherVars, setShowOtherVars] = useState(false)

  // Degisken detay modal
  const [expandedVar, setExpandedVar] = useState(null)
  const [editingSql, setEditingSql] = useState(null)
  const [varPreview, setVarPreview] = useState(null)
  const [varPreviewLoading, setVarPreviewLoading] = useState(false)
  const [columnAliases, setColumnAliases] = useState({})
  const [valueMappings, setValueMappings] = useState({}) // { "ISLEM_TURU": { "alnmalfat": "Alinan Mal Faturasi", ... } }
  const [editingCell, setEditingCell] = useState(null) // { row, col }
  const [editingCellValue, setEditingCellValue] = useState('')
  const [tableSearch, setTableSearch2] = useState('')
  const [excludedRows, setExcludedRows] = useState(new Set()) // Cikarilacak satirlar (ilk kolon degeri)

  // Canli onizleme verisi
  const [widgetData, setWidgetData] = useState({})

  useEffect(() => {
    loadAll()
  }, [id])

  const loadAll = async () => {
    try {
      const { data } = await api.get(`/dashboards/${id}`)
      setDashboard(data)
      if (data.pages.length > 0) {
        buildLayouts(data.pages[0])
        loadWidgetData(data.pages[0])
      }
      // Dashboard'un company_id'si ile datasource ve query'leri yukle
      const cid = data.company_id
      loadDatasources(cid)
      loadQueries(cid)
    } catch (err) { toast.error('Yuklenemedi') }
  }

  const loadDashboard = async () => {
    try {
      const { data } = await api.get(`/dashboards/${id}`)
      setDashboard(data)
      if (data.pages.length > 0) {
        buildLayouts(data.pages[0])
        loadWidgetData(data.pages[0])
      }
    } catch (err) { toast.error('Yuklenemedi') }
  }

  const loadWidgetData = async (page) => {
    // variable_id olan VEYA tablo modu (datasource_id + table_name) olan widget'lar
    const widgetIds = page.widgets.filter(w => w.variable_id || (w.config?.datasource_id && w.config?.table_name) || w.config?._tank_data || w.config?._pivot).map(w => w.id)
    if (widgetIds.length === 0) return
    try {
      const { data } = await api.post('/widgets/batch-data', { widget_ids: widgetIds })
      setWidgetData(data)
    } catch (e) {}
  }

  const loadDatasources = async (companyId) => {
    try {
      const params = companyId ? { company_id: companyId } : {}
      const { data } = await api.get('/datasources', { params })
      setDatasources(data)
      setSchemas({})
      // Her DS icin semayi yukle
      for (const ds of data) {
        try {
          const schema = await api.get(`/datasources/${ds.id}/schema`)
          setSchemas(prev => ({ ...prev, [ds.id]: schema.data }))
        } catch (e) {}
      }
    } catch (e) {}
  }

  const loadQueries = async (companyId) => {
    try {
      const params = companyId ? { company_id: companyId } : {}
      const [qRes, vRes] = await Promise.all([
        api.get('/queries', { params }),
        api.get('/queries/variables', { params }),
      ])
      setQueries(qRes.data)
      setVariables(vRes.data)
    } catch (e) {}
  }

  const buildLayouts = (page) => {
    const lg = page.widgets.map(w => ({
      i: String(w.id), x: w.grid.x, y: w.grid.y, w: w.grid.w, h: w.grid.h, minW: 2, minH: 2,
    }))
    setLayouts({ lg, md: lg, sm: lg })
  }

  // Widget islemleri
  const addWidget = async (widgetType) => {
    const page = dashboard?.pages[activePage]
    if (!page) { toast.error('Once bir sayfa ekleyin'); return }
    const wt = WIDGET_TYPES.find(w => w.type === widgetType)
    try {
      await api.post('/widgets', {
        page_id: page.id, widget_type: widgetType, title: wt.label,
        grid_w: wt.defaultW, grid_h: wt.defaultH, config: {},
      })
      toast.success('Widget eklendi')
      setShowAddWidget(false)
      loadDashboard()
    } catch (err) { toast.error('Eklenemedi') }
  }

  const updateWidget = async () => {
    if (!selectedWidget) return
    try {
      await api.put(`/widgets/${selectedWidget.id}`, {
        title: selectedWidget.title,
        variable_id: selectedWidget.variable_id,
        widget_type: selectedWidget.widget_type,
        config: selectedWidget.config,
      })
      toast.success('Widget guncellendi')
      // Dashboard yeniden yukle ve secili widget'i guncelle
      const { data } = await api.get(`/dashboards/${id}`)
      setDashboard(data)
      if (data.pages[activePage]) {
        buildLayouts(data.pages[activePage])
        loadWidgetData(data.pages[activePage])
        // Secili widget'i guncellenmis haliyle bul
        const updated = data.pages[activePage].widgets.find(w => w.id === selectedWidget.id)
        if (updated) setSelectedWidget(updated)
      }
    } catch (err) {
      console.error('Widget update error:', err)
      toast.error('Kaydetme hatasi: ' + (err.response?.data?.detail || err.message))
    }
  }

  const deleteWidget = async (wid) => {
    try { await api.delete(`/widgets/${wid}`); toast.success('Silindi'); setSelectedWidget(null); loadDashboard() }
    catch (e) { toast.error('Silinemedi') }
  }

  const handleLayoutChange = async (layout) => {
    const updates = layout.map(l => ({ id: parseInt(l.i), x: l.x, y: l.y, w: l.w, h: l.h }))
    try { await api.put('/widgets/batch-layout', { layouts: updates }) } catch (e) {}
  }

  // Sayfa islemleri
  const addPage = async () => {
    if (!pageName.trim()) return
    try {
      await api.post('/dashboards/pages', { dashboard_id: parseInt(id), name: pageName, order: dashboard.pages.length })
      toast.success('Sayfa eklendi'); setShowPageForm(false); setPageName(''); loadDashboard()
    } catch (e) { toast.error('Eklenemedi') }
  }

  const deletePage = async (pageId) => {
    if (!confirm('Sayfa silinecek!')) return
    try { await api.delete(`/dashboards/pages/${pageId}`); toast.success('Silindi'); setActivePage(0); loadDashboard() }
    catch (e) { toast.error('Silinemedi') }
  }

  const togglePageActive = async (page) => {
    try {
      await api.put(`/dashboards/pages/${page.id}`, {
        dashboard_id: parseInt(id),
        name: page.name,
        icon: page.icon,
        order: page.order,
        is_active: !(page.is_active !== false), // mevcut tersi
      })
      toast.success(page.is_active === false ? 'Sayfa aktif edildi' : 'Sayfa gizlendi')
      loadDashboard()
    } catch (e) { toast.error('Guncellenemedi') }
  }

  // Hizli sorgu + degisken olustur (tablodan direkt)
  const quickCreateFromTable = async (dsId, tableName, columns) => {
    const page = dashboard?.pages[activePage]
    if (!page) { toast.error('Once sayfa ekleyin'); return }

    const ds = datasources.find(d => d.id === dsId)
    const colNames = columns.map(c => c.name).join(', ')
    const limit = ds?.ds_type === 'firebird' ? `FIRST 500 ${colNames}` : colNames
    const sql = ds?.ds_type === 'firebird'
      ? `SELECT FIRST 500 ${colNames} FROM ${tableName}`
      : `SELECT ${colNames} FROM ${tableName} LIMIT 500`

    try {
      const q = await api.post('/queries', { datasource_id: dsId, name: `${tableName}_auto`, sql_text: sql })
      const v = await api.post('/queries/variables', { query_id: q.data.id, name: `${tableName}_data` })
      toast.success(`${tableName} icin veri baglantisi olusturuldu`)
      loadQueries()
      return v.data.id
    } catch (e) { toast.error('Olusturulamadi'); return null }
  }

  // Widget'a degisken bagla
  const setWidgetVariable = (varId) => {
    if (!selectedWidget) return
    setSelectedWidget({ ...selectedWidget, variable_id: varId })
  }

  // Config guncelle
  const updateConfig = (key, value) => {
    if (!selectedWidget) return
    setSelectedWidget({
      ...selectedWidget,
      config: { ...selectedWidget.config, [key]: value }
    })
  }

  const publishDashboard = async () => {
    try {
      const { data } = await api.post(`/dashboards/${id}/publish`)
      toast.success(data.is_published ? 'Yayinlandi' : 'Yayindan kaldirildi')
      loadDashboard()
    } catch (e) { toast.error('Hata') }
  }

  const saveAsTemplate = async () => {
    const name = prompt('Sablon adi:')
    if (!name) return
    try { await api.post('/templates/save', { name, dashboard_id: parseInt(id), category: 'genel' }); toast.success('Sablon kaydedildi') }
    catch (e) { toast.error('Hata') }
  }

  const currentPage = dashboard?.pages[activePage]

  // Dashboard adini yerinde duzenleme
  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const saveName = async () => {
    const yeni = (nameDraft || '').trim()
    setEditingName(false)
    if (!yeni || yeni === dashboard?.name) return
    try {
      await api.put(`/dashboards/${id}`, { name: yeni })
      setDashboard(d => ({ ...d, name: yeni }))
      toast.success('Ad güncellendi')
    } catch { toast.error('Ad güncellenemedi') }
  }

  // Field arama filtresi
  const filterFields = (tables) => {
    if (!fieldSearch) return tables
    return tables.map(t => ({
      ...t,
      columns: t.columns.filter(c =>
        c.name.toLowerCase().includes(fieldSearch.toLowerCase()) ||
        (c.display_name || '').toLowerCase().includes(fieldSearch.toLowerCase())
      )
    })).filter(t => t.columns.length > 0 || t.name.toLowerCase().includes(fieldSearch.toLowerCase()))
  }

  return (
    <div className="h-full flex bg-gray-100">
      {/* SOL PANEL — Sayfalar + Widget Listesi */}
      <div className="w-52 bg-white border-r flex flex-col flex-shrink-0">
        <div className="p-3 border-b">
          {editingName ? (
            <input autoFocus value={nameDraft} onChange={e => setNameDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false) }}
              onBlur={saveName}
              className="w-full text-sm font-semibold text-gray-800 border border-primary-300 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-primary-400" />
          ) : (
            <h2 className="font-semibold text-sm text-gray-800 flex items-center gap-1 group">
              <span className="truncate">{dashboard?.name || 'Dashboard'}</span>
              <button onClick={() => { setNameDraft(dashboard?.name || ''); setEditingName(true) }}
                title="Adı düzenle" className="text-gray-300 hover:text-primary-600 flex-shrink-0">
                <FiEdit2 size={12} />
              </button>
            </h2>
          )}
          <div className="flex gap-1 mt-1.5">
            <button onClick={publishDashboard} className="text-[10px] px-1.5 py-0.5 bg-green-50 text-green-700 rounded hover:bg-green-100">
              <FiEye className="inline mr-0.5" size={9} />{dashboard?.is_published ? 'Kapat' : 'Yayinla'}
            </button>
            <button onClick={saveAsTemplate} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100">
              <FiCopy className="inline mr-0.5" size={9} />Sablon
            </button>
            <button onClick={() => navigate(`/dashboard/${id}`)} className="text-[10px] px-1.5 py-0.5 bg-gray-50 text-gray-600 rounded hover:bg-gray-100">
              <FiEye className="inline mr-0.5" size={9} />Onizle
            </button>
          </div>
        </div>

        {/* Sayfalar */}
        <div className="p-2 border-b">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-medium text-gray-400 uppercase">Sayfalar</span>
            <button onClick={() => setShowPageForm(true)} className="text-primary-600"><FiPlus size={12} /></button>
          </div>
          {dashboard?.pages.map((page, i) => {
            const isInactive = page.is_active === false
            return (
              <div key={page.id}
                className={`flex items-center justify-between px-2 py-1 rounded text-xs cursor-pointer ${i === activePage ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'} ${isInactive ? 'opacity-50 line-through' : ''}`}
                onClick={() => { setActivePage(i); buildLayouts(page); loadWidgetData(page); setSelectedWidget(null) }}>
                <span className="truncate">{page.name}</span>
                <div className="flex items-center gap-1">
                  <button onClick={(e) => { e.stopPropagation(); togglePageActive(page) }}
                    title={isInactive ? 'Aktif et (gosteri lebilir)' : 'Pasif yap (kullanicilardan gizle)'}
                    className={isInactive ? 'text-red-400 hover:text-green-600' : 'text-gray-300 hover:text-blue-600'}>
                    {isInactive ? <FiEyeOff size={11} /> : <FiEye size={11} />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); deletePage(page.id) }} className="text-gray-300 hover:text-red-500"><FiTrash2 size={10} /></button>
                </div>
              </div>
            )
          })}
          {showPageForm && (
            <div className="flex gap-1 mt-1">
              <input value={pageName} onChange={(e) => setPageName(e.target.value)} placeholder="Sayfa adi" className="flex-1 text-xs border rounded px-1.5 py-0.5" />
              <button onClick={addPage} className="text-[10px] bg-primary-600 text-white px-1.5 rounded">Ekle</button>
            </div>
          )}
        </div>

        {/* Widget Listesi */}
        <div className="p-2 border-b">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-medium text-gray-400 uppercase">Widgetlar</span>
            <button onClick={() => setShowAddWidget(true)} className="text-primary-600"><FiPlus size={12} /></button>
          </div>
          {currentPage?.widgets.map(w => (
            <div key={w.id}
              className={`flex items-center justify-between px-2 py-1 rounded text-xs cursor-pointer ${selectedWidget?.id === w.id ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'}`}
              onClick={() => { setSelectedWidget(w); setRightTab('settings') }}>
              <span className="truncate">{w.title || w.widget_type}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteWidget(w.id) }} className="text-gray-300 hover:text-red-500"><FiTrash2 size={10} /></button>
            </div>
          ))}
        </div>

        {/* Sol panel alt kisim — sekmeli: Degiskenler | Tablolar */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex border-b">
            <button onClick={() => setLeftTab('vars')} className={`flex-1 py-1.5 text-[10px] font-medium ${leftTab === 'vars' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-400'}`}>Degiskenler</button>
            <button onClick={() => setLeftTab('tables')} className={`flex-1 py-1.5 text-[10px] font-medium ${leftTab === 'tables' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-400'}`}>Tablolar</button>
          </div>

          {/* DEGISKENLER — sayfa bazli gruplu */}
          {leftTab === 'vars' && (
            <div className="flex-1 overflow-y-auto p-1.5">
              {/* Bu sayfanin degiskenleri */}
              {(() => {
                const pageVarIds = new Set((currentPage?.widgets || []).filter(w => w.variable_id).map(w => w.variable_id))
                const pageVars = variables.filter(v => pageVarIds.has(v.id))
                const otherVars = variables.filter(v => !pageVarIds.has(v.id))
                return (
                  <>
                    {pageVars.length > 0 && (
                      <div className="mb-2">
                        <span className="text-[9px] font-semibold text-green-600 uppercase px-1">Bu Sayfada</span>
                        {pageVars.map(v => (
                          <VarItem key={v.id} v={v} isActive={selectedWidget?.variable_id === v.id}
                            onSelect={() => { if (selectedWidget) setWidgetVariable(v.id) }}
                            onEdit={() => { const q = queries.find(qq => qq.id === v.query_id); setExpandedVar(v); setEditingSql(q?.sql_text || ''); setVarPreview(null); setColumnAliases({}); setExcludedRows(new Set()) }} />
                        ))}
                      </div>
                    )}
                    {otherVars.length > 0 && (
                      <div>
                        <button onClick={() => setShowOtherVars(!showOtherVars)} className="text-[9px] font-semibold text-gray-400 uppercase px-1 flex items-center gap-1 w-full">
                          {showOtherVars ? <FiChevronDown size={9} /> : <FiChevronRight size={9} />} Diger ({otherVars.length})
                        </button>
                        {showOtherVars && otherVars.map(v => (
                          <VarItem key={v.id} v={v} isActive={selectedWidget?.variable_id === v.id}
                            onSelect={() => { if (selectedWidget) setWidgetVariable(v.id) }}
                            onEdit={() => { const q = queries.find(qq => qq.id === v.query_id); setExpandedVar(v); setEditingSql(q?.sql_text || ''); setVarPreview(null); setColumnAliases({}); setExcludedRows(new Set()) }} />
                        ))}
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          )}

          {/* TABLOLAR — veri kaynagindan gelen tablolar ve kolonlari */}
          {leftTab === 'tables' && (
            <div className="flex-1 overflow-y-auto p-1.5">
              <div className="relative mb-1.5">
                <FiSearch size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-300" />
                <input value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)}
                  placeholder="Tablo veya kolon ara..." className="w-full pl-6 pr-2 py-0.5 text-[10px] border rounded bg-gray-50" />
              </div>
              {datasources.map(ds => {
                const schema = schemas[ds.id]
                if (!schema) return null
                const tables = fieldSearch
                  ? schema.tables.filter(t => t.name.toLowerCase().includes(fieldSearch.toLowerCase()) || t.columns.some(c => c.name.toLowerCase().includes(fieldSearch.toLowerCase()))).slice(0, 20)
                  : schema.tables.slice(0, 30)
                return (
                  <div key={ds.id} className="mb-1">
                    <div className="text-[9px] font-semibold text-primary-600 uppercase px-1 mb-0.5">{ds.name}</div>
                    {tables.map(table => {
                      const tKey = `${ds.id}_${table.id}`
                      const isOpen = expandedTable[tKey]
                      const filteredCols = fieldSearch ? table.columns.filter(c => c.name.toLowerCase().includes(fieldSearch.toLowerCase())) : table.columns
                      return (
                        <div key={table.id}>
                          <button onClick={() => setExpandedTable(prev => ({ ...prev, [tKey]: !prev[tKey] }))}
                            className="w-full flex items-center gap-1 px-1 py-0.5 text-[10px] text-gray-600 hover:bg-gray-50 rounded">
                            {isOpen ? <FiChevronDown size={9} /> : <FiChevronRight size={9} />}
                            <FiDatabase size={9} className="text-gray-400" />
                            <span className="truncate">{table.name}</span>
                            <span className="text-[8px] text-gray-300 ml-auto">{table.columns.length}</span>
                          </button>
                          {isOpen && (
                            <div className="ml-4">
                              {filteredCols.map(col => {
                                const Icon = TYPE_ICONS[col.col_type] || FiType
                                return (
                                  <div key={col.id}
                                    className="flex items-center gap-1 px-1 py-0.5 text-[10px] text-gray-500 hover:bg-blue-50 rounded cursor-pointer"
                                    onClick={() => {
                                      if (!selectedWidget) { toast.error('Once bir widget secin'); return }
                                      const config = selectedWidget.config || {}
                                      if (col.col_type === 'number' || col.is_measure) {
                                        const yCols = [...(config.y_columns || [])]
                                        if (!yCols.includes(col.name)) yCols.push(col.name)
                                        updateConfig('y_columns', yCols)
                                        if (selectedWidget.widget_type === 'kpi') updateConfig('value_column', col.name)
                                        toast.success(`${col.name} → Deger`)
                                      } else {
                                        updateConfig('x_column', col.name)
                                        if (selectedWidget.widget_type === 'pie') updateConfig('label_column', col.name)
                                        toast.success(`${col.name} → Eksen`)
                                      }
                                    }}>
                                    <Icon size={9} className={col.is_measure ? 'text-blue-400' : 'text-gray-300'} />
                                    <span className="truncate">{col.name}</span>
                                    <span className="text-[8px] text-gray-300 ml-auto hidden group-hover:inline">{col.is_measure ? 'deger' : 'eksen'}</span>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ORTA — Canvas */}
      <div className="flex-1 overflow-auto p-3">
        {currentPage && layouts.lg ? (
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768 }}
            cols={{ lg: 12, md: 12, sm: 6 }}
            rowHeight={80}
            isDraggable={true}
            isResizable={true}
            onLayoutChange={handleLayoutChange}
            draggableHandle=".widget-drag-handle"
            compactType="vertical"
            margin={[8, 8]}
          >
            {currentPage.widgets.map(w => {
              const wData = widgetData[String(w.id)] || widgetData[w.id]
              const hasData = wData?.data?.length > 0
              return (
                <div key={String(w.id)}
                  onClick={() => { setSelectedWidget(w); setRightTab('settings') }}
                  className={`bg-white rounded-xl border-2 overflow-hidden flex flex-col cursor-pointer transition-colors ${
                    selectedWidget?.id === w.id ? 'border-primary-400 shadow-lg' : 'border-gray-100 hover:border-primary-200'
                  }`}>
                  <div className="widget-drag-handle cursor-move px-2 py-1 border-b border-gray-50 bg-gray-50 flex items-center justify-between"
                    title={w.config?._source_tables ? `Tablolar: ${w.config._source_tables.join(', ')}\nKolonlar: ${(w.config._source_columns || []).join(', ')}` : 'Veri bagli degil'}>
                    <span className="text-[10px] font-medium text-gray-500 truncate">{w.title || w.widget_type}</span>
                    {w.config?._source_tables && <span className="text-[8px] text-blue-400 truncate ml-1">{w.config._source_tables[0]}</span>}
                    {!w.variable_id && <span className="text-[9px] text-orange-400">veri yok</span>}
                    {hasData && <span className="text-[9px] text-gray-300">({wData.row_count})</span>}
                  </div>
                  <div className="flex-1 min-h-0">
                    {hasData ? (
                      <ChartWidget type={w.widget_type} data={wData.data} config={w.config || {}} title={w.title} widgetId={w.id} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-300 text-xs">
                        {WIDGET_TYPES.find(wt => wt.type === w.widget_type)?.icon || '📊'}
                        <span className="ml-1 text-[10px]">{w.variable_id ? 'Yukleniyor...' : 'Veri bagla →'}</span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </ResponsiveGridLayout>
        ) : (
          <div className="text-center text-gray-400 py-20">Soldan bir sayfa ekleyin</div>
        )}
      </div>

      {/* SAG PANEL — Fields + Settings */}
      <div className="w-72 bg-white border-l flex flex-col flex-shrink-0">
        {/* Tab basliklar */}
        <div className="flex border-b">
          <button onClick={() => setRightTab('fields')}
            className={`flex-1 py-2 text-xs font-medium ${rightTab === 'fields' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-400'}`}>
            Alanlar
          </button>
          <button onClick={() => setRightTab('settings')}
            className={`flex-1 py-2 text-xs font-medium ${rightTab === 'settings' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-400'}`}>
            Ayarlar
          </button>
        </div>

        {/* ALANLAR (Fields) paneli */}
        {rightTab === 'fields' && (
          <div className="flex-1 overflow-y-auto">
            <div className="p-2 border-b">
              <div className="relative">
                <FiSearch size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-300" />
                <input value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)}
                  placeholder="Alan ara..." className="w-full pl-7 pr-2 py-1 text-xs border rounded bg-gray-50" />
              </div>
            </div>
            <div className="p-1">
              {datasources.map(ds => {
                const schema = schemas[ds.id]
                if (!schema) return null
                const tables = filterFields(schema.tables)
                const isExpanded = expandedDs[ds.id]

                return (
                  <div key={ds.id} className="mb-1">
                    <button onClick={() => setExpandedDs(prev => ({ ...prev, [ds.id]: !prev[ds.id] }))}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded">
                      {isExpanded ? <FiChevronDown size={11} /> : <FiChevronRight size={11} />}
                      <FiDatabase size={11} className="text-primary-500" />
                      <span className="truncate">{ds.name}</span>
                      <span className="text-[9px] text-gray-300 ml-auto">{schema.tables.length}</span>
                    </button>

                    {isExpanded && tables.map(table => {
                      const tExpanded = expandedTable[`${ds.id}_${table.id}`]
                      return (
                        <div key={table.id} className="ml-3">
                          <button onClick={() => setExpandedTable(prev => ({ ...prev, [`${ds.id}_${table.id}`]: !prev[`${ds.id}_${table.id}`] }))}
                            className="w-full flex items-center gap-1 px-1.5 py-1 text-[11px] text-gray-600 hover:bg-gray-50 rounded">
                            {tExpanded ? <FiChevronDown size={10} /> : <FiChevronRight size={10} />}
                            <span className="truncate">{table.display_name || table.name}</span>
                            <span className="text-[9px] text-gray-300 ml-auto">{table.columns.length}</span>
                          </button>

                          {tExpanded && (
                            <div className="ml-4">
                              {/* Hizli tablo bagla butonu */}
                              <button onClick={async () => {
                                const varId = await quickCreateFromTable(ds.id, table.name, table.columns.slice(0, 10))
                                if (varId && selectedWidget) {
                                  setWidgetVariable(varId)
                                  toast.success('Widget veri baglantisi yapildi')
                                }
                              }}
                                className="w-full text-left text-[9px] text-primary-600 px-1.5 py-0.5 hover:bg-primary-50 rounded mb-0.5">
                                + Tabloyu widgeta bagla
                              </button>

                              {table.columns.map(col => {
                                const Icon = TYPE_ICONS[col.col_type] || FiType
                                return (
                                  <div key={col.id}
                                    className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-gray-600 hover:bg-blue-50 rounded cursor-pointer group"
                                    onClick={() => {
                                      if (!selectedWidget) return
                                      const config = selectedWidget.config || {}
                                      // Akilli atama: sayi -> y_columns, metin/tarih -> x_column
                                      if (col.col_type === 'number' || col.is_measure) {
                                        const yCols = [...(config.y_columns || [])]
                                        if (!yCols.includes(col.name)) yCols.push(col.name)
                                        updateConfig('y_columns', yCols)
                                        if (selectedWidget.widget_type === 'kpi') updateConfig('value_column', col.name)
                                        toast.success(`${col.name} → Deger olarak eklendi`)
                                      } else {
                                        updateConfig('x_column', col.name)
                                        if (selectedWidget.widget_type === 'pie') updateConfig('label_column', col.name)
                                        toast.success(`${col.name} → Eksen olarak eklendi`)
                                      }
                                    }}
                                    title={`Tikla: ${col.is_measure ? 'Deger' : 'Eksen'} olarak ekle`}>
                                    <Icon size={10} className={col.is_measure ? 'text-blue-400' : 'text-gray-300'} />
                                    <span className="truncate flex-1">{col.display_name || col.name}</span>
                                    <span className="hidden group-hover:inline text-[8px] text-primary-500">{col.is_measure ? '+deger' : '+eksen'}</span>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* AYARLAR paneli — Power BI tarzi */}
        {rightTab === 'settings' && selectedWidget && (
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {/* Kaynak Bilgisi */}
            {selectedWidget.config?._source_tables && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                <div className="text-[10px] font-semibold text-blue-600 mb-1">Veri Kaynagi</div>
                <div className="text-[10px] text-blue-700">
                  <span className="font-medium">Tablolar: </span>{selectedWidget.config._source_tables.join(', ')}
                </div>
                {selectedWidget.config._source_columns?.length > 0 && (
                  <div className="text-[10px] text-blue-700 mt-0.5">
                    <span className="font-medium">Kolonlar: </span>{selectedWidget.config._source_columns.join(', ')}
                  </div>
                )}
                {selectedWidget.config._query_name && (
                  <div className="text-[10px] text-blue-500 mt-0.5">Sorgu: {selectedWidget.config._query_name}</div>
                )}
              </div>
            )}

            {/* Baslik + Tip */}
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Baslik</label>
              <input value={selectedWidget.title || ''} onChange={(e) => setSelectedWidget({ ...selectedWidget, title: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Grafik Tipi</label>
              <select value={selectedWidget.widget_type} onChange={(e) => setSelectedWidget({ ...selectedWidget, widget_type: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1">
                {WIDGET_TYPES.map(wt => <option key={wt.type} value={wt.type}>{wt.icon} {wt.label}</option>)}
              </select>
            </div>

            {/* Veri Kaynagi — Tablo veya Degisken */}
            <div>
              <div className="flex items-center gap-1 mb-0.5">
                <label className="text-[10px] text-gray-500">Veri Kaynagi</label>
                <div className="flex bg-gray-100 rounded overflow-hidden ml-auto">
                  <button onClick={() => {
                    // Tablo moduna gec — variable_id temizle
                    if (selectedWidget.variable_id) {
                      setSelectedWidget({ ...selectedWidget, variable_id: null })
                    }
                  }}
                    className={`text-[9px] px-1.5 py-0.5 ${!selectedWidget.variable_id ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-200'}`}>Tablo</button>
                  <button onClick={() => {
                    // Degisken moduna gec — tablo bilgisini temizle
                    const cfg = { ...selectedWidget.config }
                    delete cfg.datasource_id
                    delete cfg.table_name
                    setSelectedWidget({ ...selectedWidget, config: cfg })
                  }}
                    className={`text-[9px] px-1.5 py-0.5 ${selectedWidget.variable_id ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-200'}`}>Degisken</button>
                </div>
              </div>

              {/* TABLO MODU — datasource + tablo sec */}
              {!selectedWidget.variable_id && (() => {
                // Tek datasource varsa otomatik sec
                const activeDsId = selectedWidget.config?.datasource_id || (datasources.length === 1 ? datasources[0].id : null)
                const activeSchema = activeDsId ? schemas[activeDsId] : null
                // Tablo arama
                const tblSearch = tableSearch; const setTblSearch = setTableSearch2
                const filteredTables = activeSchema?.tables
                  ? (tblSearch ? activeSchema.tables.filter(t => t.name.toLowerCase().includes(tblSearch.toLowerCase())) : activeSchema.tables).slice(0, 50)
                  : []

                return (
                <div className="space-y-1">
                  {datasources.length > 1 ? (
                    <select value={activeDsId || ''} onChange={(e) => {
                      const dsId = e.target.value ? parseInt(e.target.value) : null
                      updateConfig('datasource_id', dsId)
                      updateConfig('table_name', '')
                    }}
                      className="w-full text-[10px] border rounded px-2 py-1">
                      <option value="">Veri kaynagi sec...</option>
                      {datasources.map(ds => <option key={ds.id} value={ds.id}>{ds.name}</option>)}
                    </select>
                  ) : datasources.length === 1 ? (
                    <div className="text-[9px] text-primary-600 bg-primary-50 rounded px-2 py-0.5 font-medium">
                      {datasources[0].name}
                    </div>
                  ) : (
                    <div className="text-[9px] text-red-500">Veri kaynagi bulunamadi</div>
                  )}
                  {activeDsId && activeSchema && (
                    <>
                      <div className="relative">
                        <FiSearch size={9} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-300" />
                        <input value={tblSearch} onChange={(e) => setTblSearch(e.target.value)}
                          placeholder="Tablo ara..." className="w-full text-[10px] border rounded pl-5 pr-1.5 py-1" />
                      </div>
                      <select value={selectedWidget.config?.table_name || ''} onChange={(e) => {
                        updateConfig('table_name', e.target.value)
                        // datasource_id'yi de kaydet
                        if (!selectedWidget.config?.datasource_id) updateConfig('datasource_id', activeDsId)
                      }}
                        className="w-full text-[10px] border rounded px-2 py-1" size={Math.min(filteredTables.length + 1, 8)}>
                        <option value="">Tablo sec...</option>
                        {filteredTables.map(t => (
                          <option key={t.id} value={t.name}>{t.name} ({t.columns.length})</option>
                        ))}
                      </select>
                    </>
                  )}
                  {selectedWidget.config?.table_name && (
                    <div className="text-[9px] text-green-600 bg-green-50 rounded px-2 py-0.5">
                      {selectedWidget.config.table_name} secildi
                    </div>
                  )}
                </div>
                )
              })()}

              {/* DEGISKEN MODU — eski yontem */}
              {selectedWidget.variable_id !== null && selectedWidget.variable_id !== undefined && (
                <select value={selectedWidget.variable_id || ''} onChange={(e) => setWidgetVariable(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full text-xs border rounded px-2 py-1">
                  <option value="">Seciniz...</option>
                  {variables.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              )}
            </div>

            <hr className="border-gray-100" />

            {/* Secili widget icin kullanilabilir kolonlar */}
            {(() => {
              const wData = widgetData[String(selectedWidget.id)] || widgetData[selectedWidget.id]
              const cols = []
              const cfgDsId = selectedWidget.config?.datasource_id
              const cfgTable = selectedWidget.config?.table_name
              if (cfgDsId && cfgTable && schemas[cfgDsId]) {
                const table = schemas[cfgDsId]?.tables?.find(t => t.name === cfgTable)
                if (table) table.columns.forEach(c => cols.push({ name: c.name, col_type: c.col_type || 'text', source: 'widget' }))
              }
              // JOIN tablolarindan kolonlar
              ;(selectedWidget.config?.joins || []).forEach(j => {
                const jSchema = cfgDsId && schemas[cfgDsId]
                const jTable = jSchema?.tables?.find(t => t.name === j.table)
                if (jTable) jTable.columns.forEach(c => {
                  if (!cols.find(x => x.name === c.name)) cols.push({ name: c.name, col_type: c.col_type || 'text', source: j.table })
                })
              })
              // Hesaplanan kolonlar
              ;(selectedWidget.config?.calculated_columns || []).forEach(cc => {
                if (!cols.find(x => x.name === cc.name)) cols.push({ name: cc.name, col_type: 'number', source: 'hesaplanan' })
              })
              if (cols.length === 0 && wData?.columns) {
                wData.columns.forEach(c => {
                  const ct = c.type?.includes('int') || c.type?.includes('float') ? 'number' : c.type?.includes('date') ? 'date' : 'text'
                  cols.push({ name: c.name, col_type: ct, source: 'widget' })
                })
              } else if (cols.length === 0 && wData?.data?.[0]) {
                Object.keys(wData.data[0]).forEach(name => {
                  const v = wData.data[0][name]
                  const ct = typeof v === 'number' ? 'number' : (typeof v === 'string' && /^\d{4}-\d{2}/.test(v)) ? 'date' : 'text'
                  cols.push({ name, col_type: ct, source: 'widget' })
                })
              }
              Object.values(schemas).forEach(schema => {
                schema.tables?.forEach(t => {
                  t.columns?.forEach(c => {
                    if (!cols.find(x => x.name === c.name)) cols.push({ name: c.name, col_type: c.col_type || 'text', source: t.name })
                  })
                })
              })
              const availableColumns = cols
              return (
              <>
            {/* SATIRLAR (Rows) — Power BI tarzi drop zone */}
            <FieldDropZone label="Satirlar / Eksen" icon="☰"
              items={selectedWidget.config?.x_column ? [selectedWidget.config.x_column] : []}
              onAdd={(col) => updateConfig('x_column', col)}
              onRemove={() => updateConfig('x_column', '')}
              color="bg-blue-50 border-blue-200"
              availableColumns={availableColumns}
            />

            {/* DEGERLER (Values) */}
            <FieldDropZone label="Degerler" icon="Σ"
              items={selectedWidget.config?.y_columns || (selectedWidget.config?.value_column ? [selectedWidget.config.value_column] : [])}
              onAdd={(col) => {
                if (selectedWidget.widget_type === 'kpi') {
                  updateConfig('value_column', col)
                } else {
                  const yCols = [...(selectedWidget.config?.y_columns || [])]
                  if (!yCols.includes(col)) yCols.push(col)
                  updateConfig('y_columns', yCols)
                }
              }}
              onRemove={(item) => {
                if (selectedWidget.widget_type === 'kpi') {
                  updateConfig('value_column', '')
                } else {
                  updateConfig('y_columns', (selectedWidget.config?.y_columns || []).filter(c => c !== item))
                }
              }}
              color="bg-green-50 border-green-200"
              availableColumns={availableColumns}
            />

            {/* ETIKETLER (pasta/donut icin) */}
            {(selectedWidget.widget_type === 'pie' || selectedWidget.widget_type === 'donut') && (
              <FieldDropZone label="Etiket" icon="🏷"
                items={selectedWidget.config?.label_column ? [selectedWidget.config.label_column] : []}
                onAdd={(col) => updateConfig('label_column', col)}
                onRemove={() => updateConfig('label_column', '')}
                color="bg-purple-50 border-purple-200"
                availableColumns={availableColumns}
              />
            )}

            {/* TABLO KOLONLARI */}
            {selectedWidget.widget_type === 'table' && (
              <FieldDropZone label="Gorunecek Kolonlar" icon="📋"
                items={selectedWidget.config?.columns || []}
                onAdd={(col) => {
                  const cols = [...(selectedWidget.config?.columns || [])]
                  if (!cols.includes(col)) cols.push(col)
                  updateConfig('columns', cols)
                }}
                onRemove={(item) => updateConfig('columns', (selectedWidget.config?.columns || []).filter(c => c !== item))}
                color="bg-gray-50 border-gray-200"
                availableColumns={availableColumns}
              />
            )}

            {/* GROUP BY */}
            {!selectedWidget.variable_id && selectedWidget.config?.table_name && selectedWidget.config?.aggregation && (
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">Gruplama (GROUP BY)</label>
                <FieldDropZone label="Gruplama Kolonlari" icon="⊞"
                  items={selectedWidget.config?.group_by || []}
                  onAdd={(col) => {
                    const g = [...(selectedWidget.config?.group_by || [])]
                    if (!g.includes(col)) g.push(col)
                    updateConfig('group_by', g)
                  }}
                  onRemove={(item) => updateConfig('group_by', (selectedWidget.config?.group_by || []).filter(c => c !== item))}
                  color="bg-orange-50 border-orange-200"
                  availableColumns={availableColumns}
                />
              </div>
            )}
              </>
              )
            })()}

            <hr className="border-gray-100" />

            {/* ETKILESIM MODU */}
            <div>
              <span className="text-[10px] font-semibold text-gray-500 uppercase">Diger Kartlara Etkisi</span>
              <div className="mt-1 space-y-1">
                {[
                  { value: 'filter', label: 'Tamamen Filtrele', desc: 'Tiklaninca diger kartlar filtrelenir', color: 'bg-blue-50 border-blue-200 text-blue-700' },
                  { value: 'highlight', label: 'Vurgula', desc: 'Diger kartlarda vurgu gosterir, veri degismez', color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
                  { value: 'none', label: 'Etkileme', desc: 'Bu kart diger kartlari etkilemez', color: 'bg-gray-50 border-gray-200 text-gray-600' },
                ].map(mode => (
                  <label key={mode.value}
                    className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer text-[10px] ${
                      (selectedWidget.config?.interaction_mode || 'filter') === mode.value ? mode.color + ' font-medium' : 'border-gray-100 text-gray-400 hover:bg-gray-50'
                    }`}>
                    <input type="radio" name="interaction_mode" value={mode.value}
                      checked={(selectedWidget.config?.interaction_mode || 'filter') === mode.value}
                      onChange={() => updateConfig('interaction_mode', mode.value)}
                      className="sr-only" />
                    <div>
                      <div>{mode.label}</div>
                      <div className="text-[8px] text-gray-400 font-normal">{mode.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <hr className="border-gray-100" />

            {/* KART FILTRELERI */}
            <CardFilterEditor
              filters={selectedWidget.config?.card_filters || []}
              widgetData={widgetData[String(selectedWidget.id)] || widgetData[selectedWidget.id]}
              schemas={schemas}
              datasources={datasources}
              datasourceId={selectedWidget.config?.datasource_id}
              tableName={selectedWidget.config?.table_name}
              onChange={(filters) => updateConfig('card_filters', filters)}
            />

            <hr className="border-gray-100" />

            {/* TABLO BIRLESTIR (JOIN) */}
            {!selectedWidget.variable_id && selectedWidget.config?.table_name && (
              <JoinEditor
                joins={selectedWidget.config?.joins || []}
                mainTable={selectedWidget.config?.table_name}
                schemas={schemas}
                datasourceId={selectedWidget.config?.datasource_id}
                onChange={(joins) => updateConfig('joins', joins)}
              />
            )}

            {/* HESAPLANAN KOLONLAR */}
            {!selectedWidget.variable_id && selectedWidget.config?.table_name && (
              <CalcColumnEditor
                calcColumns={selectedWidget.config?.calculated_columns || []}
                onChange={(cols) => updateConfig('calculated_columns', cols)}
              />
            )}

            <hr className="border-gray-100" />

            {/* TOPLAMA */}
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Toplama</label>
              <select value={selectedWidget.config?.aggregation || ''} onChange={(e) => updateConfig('aggregation', e.target.value)}
                className="w-full text-xs border rounded px-2 py-1">
                <option value="">Yok (ham veri)</option>
                <option value="sum">Toplam (SUM)</option>
                <option value="avg">Ortalama (AVG)</option>
                <option value="count">Sayi (COUNT)</option>
                <option value="count_distinct">Benzersiz Sayi (COUNT DISTINCT)</option>
                <option value="min">Ilk / Minimum</option>
                <option value="max">Son / Maksimum</option>
              </select>
            </div>

            {/* KPI ek ayarlar */}
            {selectedWidget.widget_type === 'kpi' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Onek</label>
                  <input value={selectedWidget.config?.prefix || ''} onChange={(e) => updateConfig('prefix', e.target.value)}
                    className="w-full text-xs border rounded px-2 py-1" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Sonek</label>
                  <input value={selectedWidget.config?.suffix || ''} onChange={(e) => updateConfig('suffix', e.target.value)}
                    className="w-full text-xs border rounded px-2 py-1" placeholder=" TL" />
                </div>
              </div>
            )}

            <button onClick={updateWidget} className="w-full bg-primary-600 text-white text-xs py-2 rounded-lg hover:bg-primary-700">
              <FiSave className="inline mr-1" size={11} /> Kaydet
            </button>
          </div>
        )}

        {rightTab === 'settings' && !selectedWidget && (
          <div className="flex-1 flex items-center justify-center text-gray-300 text-xs p-4 text-center">
            Bir widget secin veya Alanlar sekmesinden veri ekleyin
          </div>
        )}
      </div>

      {/* Widget Ekleme Modal */}
      {showAddWidget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddWidget(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-lg shadow-xl">
            <h2 className="text-base font-semibold mb-3">Widget Ekle</h2>
            <div className="grid grid-cols-3 gap-2">
              {WIDGET_TYPES.map(wt => (
                <button key={wt.type} onClick={() => addWidget(wt.type)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-200 hover:border-primary-400 hover:bg-primary-50 transition-colors">
                  <span className="text-xl">{wt.icon}</span>
                  <span className="text-[11px] text-gray-600">{wt.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Degisken Detay Modal */}
      {expandedVar && (() => {
        const query = queries.find(q => q.id === expandedVar.query_id)
        if (!query) return null
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="px-5 py-3 border-b flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold">{expandedVar.name}</h2>
                  <p className="text-[11px] text-gray-400">Sorgu: {query.name} (ID: {query.id})</p>
                </div>
                <button onClick={() => { setExpandedVar(null); setVarPreview(null); setEditingSql(null) }} className="p-1 hover:bg-gray-100 rounded"><FiX size={16} /></button>
              </div>

              <div className="flex-1 overflow-hidden flex flex-col p-5 gap-3">
                {/* SQL Editor */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">SQL Sorgusu</label>
                  <textarea value={editingSql ?? query.sql_text} onChange={(e) => setEditingSql(e.target.value)}
                    className="w-full font-mono text-xs bg-gray-900 text-green-400 p-3 rounded-lg h-24 resize-none focus:ring-2 focus:ring-primary-500 outline-none" />
                  <div className="flex gap-2 mt-2">
                    <button onClick={async () => {
                      setVarPreviewLoading(true)
                      try {
                        const { data } = await api.post('/queries/execute', {
                          datasource_id: query.datasource_id,
                          sql_text: editingSql || query.sql_text,
                          limit: 50
                        })
                        setVarPreview(data)
                        // Kolon alias'larini baslat
                        const aliases = {}
                        data.columns.forEach(c => { aliases[c.name] = columnAliases[c.name] || c.name })
                        setColumnAliases(aliases)
                      } catch (err) { toast.error('Sorgu hatasi: ' + (err.response?.data?.detail || err.message)) }
                      setVarPreviewLoading(false)
                    }} disabled={varPreviewLoading}
                      className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 disabled:opacity-50">
                      {varPreviewLoading ? 'Calistiriliyor...' : 'Calistir ve Onizle'}
                    </button>
                    <button onClick={async () => {
                      try {
                        await api.put(`/queries/${query.id}`, { datasource_id: query.datasource_id, name: query.name, sql_text: editingSql })
                        toast.success('SQL kaydedildi')
                        loadQueries()
                      } catch (err) { toast.error('Hata: ' + (err.response?.data?.detail || err.message)) }
                    }} className="text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg hover:bg-primary-700">
                      SQL Kaydet
                    </button>
                  </div>
                </div>

                {/* Sonuc Tablosu + Kolon Duzenleme */}
                {varPreview && (
                  <div className="flex-1 overflow-hidden flex flex-col border rounded-lg">
                    {/* Kolon baslik satiri — duzenlenebilir */}
                    <div className="bg-gray-50 border-b px-1 py-1 flex items-center gap-0.5 overflow-x-auto flex-shrink-0">
                      {varPreview.columns.map(col => (
                        <div key={col.name} className="flex flex-col items-center min-w-[100px] px-1">
                          <input value={columnAliases[col.name] || col.name}
                            onChange={(e) => setColumnAliases(prev => ({ ...prev, [col.name]: e.target.value }))}
                            className="text-[10px] font-medium text-center border rounded px-1 py-0.5 w-full bg-white focus:ring-1 focus:ring-primary-500"
                            title={`Orijinal: ${col.name} (${col.type})`} />
                          <span className="text-[8px] text-gray-400">{col.name}</span>
                        </div>
                      ))}
                    </div>

                    {/* Veri satirlari — checkbox + hucrelere tikla deger esleme yap */}
                    <div className="flex-1 overflow-auto">
                      {(() => {
                        const firstCol = varPreview.columns[0]?.name
                        const allRowKeys = varPreview.data.slice(0, 30).map(row => String(row[firstCol] ?? ''))
                        const allChecked = allRowKeys.every(k => !excludedRows.has(k))
                        const noneChecked = allRowKeys.every(k => excludedRows.has(k))

                        const toggleRow = (key) => {
                          setExcludedRows(prev => {
                            const next = new Set(prev)
                            if (next.has(key)) next.delete(key)
                            else next.add(key)
                            return next
                          })
                        }
                        const toggleAll = () => {
                          if (allChecked) {
                            setExcludedRows(new Set(allRowKeys))
                          } else {
                            setExcludedRows(new Set())
                          }
                        }

                        return (
                      <table className="w-full text-xs">
                        <thead className="sticky top-0">
                          <tr>
                            <th className="bg-gray-100 px-1 py-1 border-b w-8 text-center">
                              <input type="checkbox" checked={allChecked} onChange={toggleAll}
                                className="w-3 h-3 rounded border-gray-300 text-primary-600 cursor-pointer" title="Tumunu sec/kaldir" />
                            </th>
                            {varPreview.columns.map(col => (
                              <th key={col.name} className="bg-gray-100 text-left px-2 py-1 text-[10px] font-medium text-gray-500 border-b whitespace-nowrap min-w-[80px]">
                                {columnAliases[col.name] || col.name}
                              </th>
                            ))}
                            <th className="bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-400 border-b w-20">Esleme</th>
                          </tr>
                        </thead>
                        <tbody>
                          {varPreview.data.slice(0, 30).map((row, i) => {
                            const rowKey = String(row[firstCol] ?? '')
                            const isExcluded = excludedRows.has(rowKey)
                            return (
                            <tr key={i} className={`border-b border-gray-50 ${isExcluded ? 'opacity-40 bg-red-50/50' : 'hover:bg-blue-50/50'}`}>
                              <td className="px-1 py-1 text-center">
                                <input type="checkbox" checked={!isExcluded} onChange={() => toggleRow(rowKey)}
                                  className="w-3 h-3 rounded border-gray-300 text-primary-600 cursor-pointer" />
                              </td>
                              {varPreview.columns.map(col => {
                                const val = row[col.name]
                                const strVal = String(val ?? '')
                                const mapping = valueMappings[col.name]?.[strVal]
                                const isEditing = editingCell?.row === i && editingCell?.col === col.name
                                const isText = typeof val === 'string' || val === null

                                return (
                                  <td key={col.name} className="px-2 py-1 whitespace-nowrap">
                                    {isEditing ? (
                                      <input value={editingCellValue} onChange={(e) => setEditingCellValue(e.target.value)}
                                        onBlur={() => {
                                          if (editingCellValue && editingCellValue !== strVal) {
                                            setValueMappings(prev => ({
                                              ...prev,
                                              [col.name]: { ...(prev[col.name] || {}), [strVal]: editingCellValue }
                                            }))
                                          }
                                          setEditingCell(null)
                                        }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }}
                                        className="text-xs border rounded px-1 py-0.5 w-full bg-yellow-50 focus:ring-1 focus:ring-yellow-500 outline-none"
                                        autoFocus />
                                    ) : (
                                      <span
                                        className={`${isText ? 'cursor-pointer hover:bg-yellow-50 hover:outline hover:outline-1 hover:outline-yellow-300 rounded px-0.5' : ''} ${mapping ? 'text-green-700 font-medium' : 'text-gray-600'} ${isExcluded ? 'line-through' : ''}`}
                                        onClick={() => {
                                          if (isText && strVal) {
                                            setEditingCell({ row: i, col: col.name })
                                            setEditingCellValue(mapping || strVal)
                                          }
                                        }}
                                        title={mapping ? `Orijinal: ${strVal}` : isText ? 'Tikla = isim degistir' : ''}>
                                        {mapping || (val ?? '-')}
                                      </span>
                                    )}
                                  </td>
                                )
                              })}
                              <td className="px-2 py-1">
                                {varPreview.columns.some(col => valueMappings[col.name]?.[String(row[col.name] ?? '')]) && (
                                  <span className="text-[8px] text-green-500">eslemeli</span>
                                )}
                              </td>
                            </tr>
                            )
                          })}
                        </tbody>
                      </table>
                        )
                      })()}
                    </div>

                    {/* Alt bar — esleme + exclude bilgisi + uygula */}
                    <div className="px-3 py-2 bg-gray-50 border-t flex items-center justify-between flex-shrink-0 gap-2">
                      <span className="text-[10px] text-gray-400 flex-shrink-0">
                        {varPreview.row_count} satir
                        {excludedRows.size > 0 && <span className="text-red-500 ml-1">· {excludedRows.size} cikarildi</span>}
                      </span>
                      <div className="flex gap-2 flex-wrap justify-end">
                        {excludedRows.size > 0 && (
                          <button onClick={() => {
                            // Cikarilan satirlari WHERE ... NOT IN (...) olarak SQL'e ekle
                            let sql = editingSql || query.sql_text
                            const firstCol = varPreview.columns[0]?.name
                            if (!firstCol) return
                            const excludeList = [...excludedRows].map(v => `'${v.replace(/'/g, "''")}'`).join(', ')
                            const notInClause = `${firstCol} NOT IN (${excludeList})`
                            // WHERE zaten varsa AND ekle, yoksa WHERE ekle
                            const upperSql = sql.toUpperCase()
                            const whereIdx = upperSql.lastIndexOf('WHERE')
                            const groupIdx = upperSql.indexOf('GROUP BY')
                            const orderIdx = upperSql.indexOf('ORDER BY')
                            const limitIdx = upperSql.indexOf('LIMIT')
                            // En dogru yere ekle
                            if (whereIdx > -1) {
                              // WHERE var, GROUP BY/ORDER BY/LIMIT'ten onceki yere AND ekle
                              const insertBefore = [groupIdx, orderIdx, limitIdx].filter(i => i > whereIdx).sort((a,b) => a - b)[0]
                              if (insertBefore) {
                                sql = sql.slice(0, insertBefore) + `AND ${notInClause} ` + sql.slice(insertBefore)
                              } else {
                                sql += ` AND ${notInClause}`
                              }
                            } else {
                              // WHERE yok
                              const insertBefore = [groupIdx, orderIdx, limitIdx].filter(i => i > -1).sort((a,b) => a - b)[0]
                              if (insertBefore) {
                                sql = sql.slice(0, insertBefore) + `WHERE ${notInClause} ` + sql.slice(insertBefore)
                              } else {
                                sql += ` WHERE ${notInClause}`
                              }
                            }
                            setEditingSql(sql)
                            setExcludedRows(new Set())
                            toast.success(`${excludedRows.size} satir SQL'den cikarildi — "SQL Kaydet" ile kaydedebilirsin`)
                          }} className="text-[10px] bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700">
                            Secilmeyenleri Cikar
                          </button>
                        )}
                        {Object.keys(valueMappings).length > 0 && (
                          <>
                            <span className="text-[10px] text-green-600">
                              {Object.values(valueMappings).reduce((s, m) => s + Object.keys(m).length, 0)} esleme
                            </span>
                            <button onClick={() => {
                              // Eslemeleri SQL CASE WHEN olarak uygula
                              let sql = editingSql || query.sql_text
                              Object.entries(valueMappings).forEach(([col, maps]) => {
                                if (Object.keys(maps).length === 0) return
                                // Zaten CASE varsa ekleme
                                if (sql.match(new RegExp(`CASE\\s+${col}\\s+WHEN`, 'i'))) return
                                const cases = Object.entries(maps).map(([from, to]) => `WHEN '${from}' THEN '${to}'`).join(' ')
                                const caseExpr = `CASE ${col} ${cases} ELSE ${col} END AS ${col}`
                                // SELECT ile FROM arasindaki ilk eşleşmeyi değiştir (GROUP BY'dakini değil)
                                const selectIdx = sql.toUpperCase().indexOf('SELECT')
                                const fromIdx = sql.toUpperCase().indexOf('FROM')
                                if (selectIdx >= 0 && fromIdx > selectIdx) {
                                  const selectPart = sql.substring(selectIdx, fromIdx)
                                  const replaced = selectPart.replace(new RegExp(`\\b${col}\\b(?=\\s*,|\\s*FROM|\\s*$)`, 'i'), caseExpr)
                                  sql = sql.substring(0, selectIdx) + replaced + sql.substring(fromIdx)
                                }
                              })
                              setEditingSql(sql)
                              toast.success('Eslemeler SQL\'e eklendi — "SQL Kaydet" ile kaydedebilirsin')
                            }} className="text-[10px] bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">
                              Eslemeleri SQL'e Uygula
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {!varPreview && !varPreviewLoading && (
                  <div className="flex-1 flex items-center justify-center text-gray-300 text-sm border rounded-lg">
                    "Calistir ve Onizle" butonuna basarak sorgu sonucunu gorun
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}


function VarItem({ v, isActive, onSelect, onEdit }) {
  return (
    <div className={`text-[10px] px-1.5 py-1 rounded cursor-pointer mb-0.5 ${isActive ? 'bg-primary-100 text-primary-800 border border-primary-300' : 'text-gray-600 hover:bg-blue-50'}`}
      onClick={onSelect}>
      <div className="flex items-center gap-1">
        <FiDatabase size={8} className={isActive ? 'text-primary-600' : 'text-blue-400'} />
        <span className="truncate flex-1">{v.name}</span>
        {isActive && <span className="text-[7px] bg-primary-200 text-primary-700 px-1 rounded">aktif</span>}
        <button onClick={(e) => { e.stopPropagation(); onEdit() }} className="text-gray-300 hover:text-gray-500"><FiSettings size={8} /></button>
      </div>
    </div>
  )
}


function FieldDropZone({ label, icon, items, onAdd, onRemove, color, availableColumns = [] }) {
  const [adding, setAdding] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = search
    ? availableColumns.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : availableColumns

  // Zaten eklenmis kolonlari gri goster
  const itemSet = new Set(items)

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-500 uppercase flex items-center gap-1">
          <span>{icon}</span> {label}
        </span>
        <button onClick={() => { setAdding(!adding); setSearch('') }} className="text-primary-600 hover:text-primary-700">
          {adding ? <FiX size={11} /> : <FiPlus size={11} />}
        </button>
      </div>
      <div className={`min-h-[28px] rounded-lg border border-dashed p-1 space-y-0.5 ${color}`}>
        {items.length === 0 && !adding && (
          <p className="text-[9px] text-gray-300 text-center py-1 italic">+ ile kolon ekle</p>
        )}
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between bg-white rounded px-2 py-0.5 text-[10px] text-gray-700 shadow-sm">
            <span>{item}</span>
            <button onClick={() => onRemove(item)} className="text-gray-300 hover:text-red-500 ml-1"><FiX size={9} /></button>
          </div>
        ))}
        {adding && (
          <div>
            <div className="relative mb-0.5">
              <FiSearch size={9} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-300" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Kolon ara..."
                className="w-full text-[10px] border rounded pl-5 pr-1.5 py-0.5 bg-white focus:ring-1 focus:ring-primary-500 outline-none"
                autoFocus />
            </div>
            <div className="max-h-36 overflow-y-auto bg-white border rounded">
              {filtered.length > 0 ? filtered.slice(0, 40).map(col => {
                const Icon = TYPE_ICONS[col.col_type] || FiType
                const alreadyAdded = itemSet.has(col.name)
                return (
                  <button key={col.name + col.source} onClick={() => {
                    if (!alreadyAdded) {
                      onAdd(col.name)
                      setSearch('')
                      setAdding(false)
                    }
                  }}
                    className={`w-full flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-left ${
                      alreadyAdded ? 'text-gray-300 bg-gray-50' : col.source === 'widget' ? 'text-green-700 hover:bg-green-50 font-medium' : 'text-gray-600 hover:bg-blue-50'
                    }`}
                    disabled={alreadyAdded}>
                    <Icon size={9} className={col.col_type === 'number' ? 'text-blue-400' : col.col_type === 'date' ? 'text-purple-400' : 'text-gray-300'} />
                    <span className="truncate">{col.name}</span>
                    {alreadyAdded && <span className="text-[8px] text-gray-300 ml-auto">eklendi</span>}
                    {!alreadyAdded && col.source !== 'widget' && <span className="text-[8px] text-gray-300 ml-auto">{col.source}</span>}
                  </button>
                )
              }) : (
                <p className="text-[9px] text-gray-300 p-1.5 text-center">Sonuc yok</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}


const DATE_PRESETS = [
  { value: 'today', label: 'Bugun' },
  { value: 'yesterday', label: 'Dun' },
  { value: 'this_week', label: 'Bu Hafta' },
  { value: 'last_week', label: 'Gecen Hafta' },
  { value: 'last_7_days', label: 'Son 7 Gun' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Gecen Ay' },
  { value: 'last_30_days', label: 'Son 30 Gun' },
  { value: 'this_year', label: 'Bu Yil' },
  { value: 'last_year', label: 'Gecen Yil' },
  { value: 'last_365_days', label: 'Son 365 Gun' },
]

const TEXT_OPERATORS = [
  { value: 'checklist', label: 'Listeden Sec' },
  { value: '=', label: '= Esit' },
  { value: '!=', label: '!= Esit degil' },
  { value: 'LIKE', label: 'Icerir' },
  { value: 'IN', label: 'Listede' },
  { value: 'IS NULL', label: 'Bos' },
  { value: 'IS NOT NULL', label: 'Dolu' },
]

const NUMBER_OPERATORS = [
  { value: '=', label: '= Esit' },
  { value: '!=', label: '!= Esit degil' },
  { value: '>', label: '> Buyuk' },
  { value: '<', label: '< Kucuk' },
  { value: '>=', label: '>= Buyuk esit' },
  { value: '<=', label: '<= Kucuk esit' },
  { value: 'BETWEEN', label: 'Aralik' },
  { value: 'IS NULL', label: 'Bos' },
  { value: 'IS NOT NULL', label: 'Dolu' },
]

const DATE_OPERATORS = [
  { value: 'preset', label: 'Hazir Donem' },
  { value: 'BETWEEN', label: 'Tarih Aralik' },
  { value: '=', label: '= Esit' },
  { value: '>', label: '> Sonra' },
  { value: '<', label: '< Once' },
  { value: '>=', label: '>= Sonra/Esit' },
  { value: '<=', label: '<= Once/Esit' },
  { value: 'IS NULL', label: 'Bos' },
  { value: 'IS NOT NULL', label: 'Dolu' },
]

function _getFilterLabel(f) {
  if (f.preset) {
    const p = DATE_PRESETS.find(p => p.value === f.preset)
    return `${f.column} → ${p?.label || f.preset}`
  }
  if (f.operator === 'BETWEEN') return `${f.column} ${f.value} ~ ${f.value2 || '?'}`
  if (f.operator === 'IS NULL') return `${f.column} Bos`
  if (f.operator === 'IS NOT NULL') return `${f.column} Dolu`
  if (f.operator === 'NOT IN' && Array.isArray(f.value)) return `${f.column} ✕ ${f.value.length} cikarildi`
  if (f.operator === 'IN' && Array.isArray(f.value)) return `${f.column} ✓ ${f.value.length} secili`
  return `${f.column} ${f.operator} ${f.value}`
}

function _getColTypeColor(t) {
  if (t === 'date') return 'bg-purple-50 border-purple-200 text-purple-700'
  if (t === 'number') return 'bg-blue-50 border-blue-200 text-blue-700'
  return 'bg-amber-50 border-amber-200 text-amber-700'
}


function CardFilterEditor({ filters, widgetData, schemas, datasources, datasourceId, tableName, onChange }) {
  const [adding, setAdding] = useState(false)
  const [newFilter, setNewFilter] = useState({ column: '', col_type: 'text', operator: '=', value: '', value2: '', preset: '' })
  const [colSearch, setColSearch] = useState('')
  const [distinctValues, setDistinctValues] = useState([])
  const [distinctLoading, setDistinctLoading] = useState(false)
  const [checkedValues, setCheckedValues] = useState(new Set())
  const [distinctSearch, setDistinctSearch] = useState('')

  // Schema'dan kolon tip haritasi olustur (en guvenilir kaynak)
  const schemaTypeMap = {}
  if (schemas) {
    Object.values(schemas).forEach(schema => {
      schema.tables?.forEach(table => {
        table.columns?.forEach(col => {
          schemaTypeMap[col.name] = col.col_type || 'text'
        })
      })
    })
  }

  // Widget verisindeki kolonlar ONCE gelsin
  const allColumns = []
  if (widgetData?.columns) {
    widgetData.columns.forEach(c => {
      // Schema'daki tip varsa onu kullan, yoksa dtype'tan belirle
      const colType = schemaTypeMap[c.name] || (
        c.type?.includes('int') || c.type?.includes('float') ? 'number'
        : c.type?.includes('date') || c.type?.includes('time') ? 'date' : 'text'
      )
      allColumns.push({ key: `_widget_.${c.name}`, name: c.name, table: '** Bu Widget **', dsName: '', col_type: colType, display_name: c.name, isWidgetCol: true })
    })
  } else if (widgetData?.data?.[0]) {
    Object.keys(widgetData.data[0]).forEach(name => {
      const val = widgetData.data[0][name]
      const colType = schemaTypeMap[name] || (
        typeof val === 'number' ? 'number' : (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) ? 'date' : 'text'
      )
      allColumns.push({ key: `_widget_.${name}`, name, table: '** Bu Widget **', dsName: '', col_type: colType, display_name: name, isWidgetCol: true })
    })
  }
  // Sonra schema tablolarindan kolonlar
  if (schemas) {
    Object.entries(schemas).forEach(([dsId, schema]) => {
      const ds = datasources?.find(d => d.id === parseInt(dsId))
      schema.tables?.forEach(table => {
        table.columns?.forEach(col => {
          if (!allColumns.find(ac => ac.name === col.name && ac.isWidgetCol)) {
            allColumns.push({
              key: `${table.name}.${col.name}`,
              name: col.name,
              table: table.name,
              dsName: ds?.name || schema.datasource_name || '',
              col_type: col.col_type || 'text',
              display_name: col.display_name || col.name,
            })
          }
        })
      })
    })
  }

  // Arama filtresi
  const filteredCols = colSearch
    ? allColumns.filter(c => c.name.toLowerCase().includes(colSearch.toLowerCase()) || c.table.toLowerCase().includes(colSearch.toLowerCase()))
    : allColumns

  // Kolon secildiginde tipi otomatik belirle
  const selectColumn = (colKey) => {
    const col = allColumns.find(c => c.key === colKey)
    if (!col) return
    const defaultOp = col.col_type === 'date' ? 'preset' : col.col_type === 'number' ? '=' : 'checklist'
    setNewFilter({ column: col.name, col_type: col.col_type, operator: defaultOp, value: '', value2: '', preset: '' })
    setDistinctValues([])
    setCheckedValues(new Set())
    setDistinctSearch('')
    // Metin kolonu ise distinct degerleri cek
    if (col.col_type === 'text' || col.col_type === 'number') {
      loadDistinctValues(col.name)
    }
  }

  const loadDistinctValues = async (colName) => {
    setDistinctLoading(true)
    try {
      // Once widget verisinden dene
      if (widgetData?.data?.length > 0 && widgetData.data[0]?.[colName] !== undefined) {
        const vals = [...new Set(widgetData.data.map(r => r[colName]).filter(v => v != null).map(String))].sort()
        setDistinctValues(vals)
        setCheckedValues(new Set(vals))
        setDistinctLoading(false)
        return
      }
      // Yoksa API'dan cek
      if (datasourceId && tableName) {
        const { data } = await api.get(`/datasources/${datasourceId}/distinct/${tableName}/${colName}`)
        setDistinctValues(data.values || [])
        setCheckedValues(new Set(data.values || []))
      }
    } catch (e) { /* */ }
    setDistinctLoading(false)
  }

  // Secili kolon tipine gore operatorler
  const getOperators = () => {
    if (newFilter.col_type === 'date') return DATE_OPERATORS
    if (newFilter.col_type === 'number') return NUMBER_OPERATORS
    return TEXT_OPERATORS
  }

  // Metin kolonlari icin unique degerler
  const columnValues = newFilter.column && newFilter.col_type === 'text' && widgetData?.data
    ? [...new Set(widgetData.data.map(r => r[newFilter.column]).filter(v => v != null).map(String))].sort().slice(0, 50)
    : []

  const addFilter = () => {
    if (!newFilter.column) return

    // Checklist modu — isaretlenmeyenleri NOT IN olarak ekle
    if (newFilter.operator === 'checklist') {
      const unchecked = distinctValues.filter(v => !checkedValues.has(v))
      if (unchecked.length === 0) return // Hepsi secili, filtre gereksiz
      const f = { column: newFilter.column, col_type: newFilter.col_type, operator: 'NOT IN', value: unchecked }
      onChange([...filters, f])
      setNewFilter({ column: '', col_type: 'text', operator: '=', value: '', value2: '', preset: '' })
      setColSearch('')
      setDistinctValues([])
      setAdding(false)
      return
    }

    // Preset ve null/not null icin deger zorunlu degil
    const needsValue = !newFilter.preset && !['IS NULL', 'IS NOT NULL'].includes(newFilter.operator)
    if (needsValue && !newFilter.value) return

    const f = { column: newFilter.column, col_type: newFilter.col_type, operator: newFilter.operator }
    if (newFilter.preset) {
      f.preset = newFilter.preset
      f.operator = 'preset'
    } else {
      f.value = newFilter.value
      if (newFilter.operator === 'BETWEEN') f.value2 = newFilter.value2
    }
    onChange([...filters, f])
    setNewFilter({ column: '', col_type: 'text', operator: '=', value: '', value2: '', preset: '' })
    setColSearch('')
    setAdding(false)
  }

  const removeFilter = (index) => {
    const f = [...filters]
    f.splice(index, 1)
    onChange(f)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-500 uppercase">Kart Filtreleri</span>
        <button onClick={() => setAdding(!adding)} className="text-primary-600">
          {adding ? <FiX size={11} /> : <FiPlus size={11} />}
        </button>
      </div>

      {/* Mevcut filtreler */}
      {filters.map((f, i) => (
        <div key={i} className={`flex items-center gap-1 rounded px-2 py-0.5 mb-1 text-[10px] border ${_getColTypeColor(f.col_type || 'text')}`}>
          <span className="flex-1 truncate">{_getFilterLabel(f)}</span>
          <button onClick={() => removeFilter(i)} className="text-red-400 hover:text-red-600 flex-shrink-0"><FiX size={10} /></button>
        </div>
      ))}

      {filters.length === 0 && !adding && (
        <p className="text-[9px] text-gray-300 italic">Filtre yok — + ile ekle</p>
      )}

      {/* Yeni filtre ekleme formu */}
      {adding && (
        <div className="bg-gray-50 border rounded-lg p-2 mt-1 space-y-1.5">
          {/* Kolon secimi — aranabilir */}
          <div>
            <label className="text-[9px] text-gray-500">Kolon (tum tablolardan)</label>
            <div className="relative">
              <FiSearch size={9} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-300" />
              <input value={colSearch} onChange={(e) => setColSearch(e.target.value)}
                placeholder="Kolon ara..." className="w-full text-[10px] border rounded pl-5 pr-1.5 py-1" />
            </div>
            {(colSearch || !newFilter.column) && (
              <div className="max-h-40 overflow-y-auto border rounded mt-0.5 bg-white">
                {filteredCols.slice(0, 60).map((col, ci) => {
                  const Icon = TYPE_ICONS[col.col_type] || FiType
                  const prevCol = ci > 0 ? filteredCols[ci - 1] : null
                  const showSep = col.isWidgetCol !== prevCol?.isWidgetCol && ci > 0
                  return (
                    <div key={col.key}>
                      {ci === 0 && col.isWidgetCol && <div className="text-[8px] font-bold text-green-600 px-1.5 pt-1 bg-green-50">WIDGET KOLONLARI (onerilen)</div>}
                      {showSep && <div className="text-[8px] font-bold text-gray-400 px-1.5 pt-1 border-t bg-gray-50">SCHEMA KOLONLARI</div>}
                      <button onClick={() => { selectColumn(col.key); setColSearch('') }}
                        className={`w-full flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-left ${col.isWidgetCol ? 'text-green-700 bg-green-50/50 hover:bg-green-100 font-medium' : 'text-gray-600 hover:bg-blue-50'}`}>
                        <Icon size={9} className={col.col_type === 'number' ? 'text-blue-400' : col.col_type === 'date' ? 'text-purple-400' : 'text-gray-300'} />
                        <span className="truncate">{col.name}</span>
                        <span className="text-[8px] text-gray-300 ml-auto">{col.isWidgetCol ? '' : col.table}</span>
                      </button>
                    </div>
                  )
                })}
                {filteredCols.length === 0 && <p className="text-[9px] text-gray-300 p-1 text-center">Sonuc yok</p>}
              </div>
            )}
            {newFilter.column && !colSearch && (
              <div className={`mt-0.5 text-[10px] px-1.5 py-0.5 rounded border ${_getColTypeColor(newFilter.col_type)}`}>
                {newFilter.column}
                <span className="text-[8px] ml-1 opacity-60">({newFilter.col_type})</span>
                <button onClick={() => setNewFilter({ ...newFilter, column: '', col_type: 'text' })} className="ml-1 opacity-50 hover:opacity-100"><FiX size={8} /></button>
              </div>
            )}
          </div>

          {/* Operator — tipe gore degisir */}
          {newFilter.column && (
            <div>
              <label className="text-[9px] text-gray-500">
                {newFilter.col_type === 'date' ? 'Tarih Filtresi' : newFilter.col_type === 'number' ? 'Sayi Filtresi' : 'Metin Filtresi'}
              </label>
              <select value={newFilter.operator} onChange={(e) => {
                setNewFilter({ ...newFilter, operator: e.target.value, value: '', value2: '', preset: '' })
                if (e.target.value === 'checklist' && newFilter.column && distinctValues.length === 0) {
                  loadDistinctValues(newFilter.column)
                }
              }}
                className="w-full text-[10px] border rounded px-1.5 py-1">
                {getOperators().map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
            </div>
          )}

          {/* Checklist modu — degerler listesi */}
          {newFilter.column && newFilter.operator === 'checklist' && (
            <div>
              <div className="flex items-center justify-between mb-0.5">
                <label className="text-[9px] text-gray-500">Degerleri sec/kaldir</label>
                {distinctLoading && <span className="text-[8px] text-gray-400">yukleniyor...</span>}
              </div>
              {distinctValues.length > 0 && (
                <>
                  {distinctValues.length > 8 && (
                    <div className="relative mb-0.5">
                      <FiSearch size={9} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-300" />
                      <input value={distinctSearch} onChange={(e) => setDistinctSearch(e.target.value)}
                        placeholder="Ara..." className="w-full text-[10px] border rounded pl-5 pr-1.5 py-0.5" />
                    </div>
                  )}
                  <div className="flex gap-1 mb-0.5">
                    <button onClick={() => setCheckedValues(new Set(distinctValues))} className="text-[8px] text-primary-600 hover:underline">Tumunu Sec</button>
                    <button onClick={() => setCheckedValues(new Set())} className="text-[8px] text-red-500 hover:underline">Hepsini Kaldir</button>
                  </div>
                  <div className="max-h-44 overflow-y-auto border rounded bg-white">
                    {distinctValues
                      .filter(v => !distinctSearch || v.toLowerCase().includes(distinctSearch.toLowerCase()))
                      .map(val => (
                      <label key={val} className={`flex items-center gap-1.5 px-1.5 py-0.5 text-[10px] cursor-pointer hover:bg-blue-50 ${!checkedValues.has(val) ? 'text-red-500 line-through bg-red-50/50' : 'text-gray-700'}`}>
                        <input type="checkbox" checked={checkedValues.has(val)}
                          onChange={() => {
                            setCheckedValues(prev => {
                              const next = new Set(prev)
                              if (next.has(val)) next.delete(val)
                              else next.add(val)
                              return next
                            })
                          }}
                          className="w-3 h-3 rounded border-gray-300 text-primary-600" />
                        {val}
                      </label>
                    ))}
                  </div>
                  <div className="text-[8px] text-gray-400 mt-0.5">
                    {checkedValues.size}/{distinctValues.length} secili
                    {distinctValues.length - checkedValues.size > 0 && (
                      <span className="text-red-500 ml-1">({distinctValues.length - checkedValues.size} cikarilacak)</span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Deger alani — tipe ve operatore gore */}
          {newFilter.column && newFilter.operator === 'preset' && (
            <div>
              <label className="text-[9px] text-gray-500">Donem</label>
              <div className="grid grid-cols-2 gap-0.5">
                {DATE_PRESETS.map(p => (
                  <button key={p.value}
                    onClick={() => setNewFilter({ ...newFilter, preset: p.value })}
                    className={`text-[9px] px-1.5 py-1 rounded border text-left ${
                      newFilter.preset === p.value ? 'bg-purple-100 border-purple-400 text-purple-700 font-medium' : 'bg-white border-gray-200 text-gray-600 hover:bg-purple-50'
                    }`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {newFilter.column && newFilter.operator === 'BETWEEN' && (
            <div className="grid grid-cols-2 gap-1">
              <div>
                <label className="text-[9px] text-gray-500">Baslangic</label>
                <input type={newFilter.col_type === 'date' ? 'date' : 'number'} value={newFilter.value}
                  onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
                  className="w-full text-[10px] border rounded px-1.5 py-1" />
              </div>
              <div>
                <label className="text-[9px] text-gray-500">Bitis</label>
                <input type={newFilter.col_type === 'date' ? 'date' : 'number'} value={newFilter.value2 || ''}
                  onChange={(e) => setNewFilter({ ...newFilter, value2: e.target.value })}
                  className="w-full text-[10px] border rounded px-1.5 py-1" />
              </div>
            </div>
          )}

          {newFilter.column && !['preset', 'BETWEEN', 'IS NULL', 'IS NOT NULL'].includes(newFilter.operator) && (
            <div>
              <label className="text-[9px] text-gray-500">Deger</label>
              {newFilter.col_type === 'date' ? (
                <input type="date" value={newFilter.value}
                  onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
                  className="w-full text-[10px] border rounded px-1.5 py-1" />
              ) : newFilter.col_type === 'number' ? (
                <input type="number" value={newFilter.value}
                  onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
                  placeholder="Sayi gir..." className="w-full text-[10px] border rounded px-1.5 py-1" />
              ) : columnValues.length > 0 && columnValues.length <= 30 ? (
                <select value={newFilter.value} onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
                  className="w-full text-[10px] border rounded px-1.5 py-1">
                  <option value="">Deger sec...</option>
                  {columnValues.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <input value={newFilter.value} onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
                  placeholder="Deger yaz..." className="w-full text-[10px] border rounded px-1.5 py-1" />
              )}
            </div>
          )}

          {newFilter.column && (
            <div className="flex gap-1">
              <button onClick={addFilter} className="text-[9px] bg-primary-600 text-white px-2 py-0.5 rounded flex-1">Filtre Ekle</button>
              <button onClick={() => { setAdding(false); setColSearch('') }} className="text-[9px] text-gray-500 px-2 py-0.5">Iptal</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}


function JoinEditor({ joins, mainTable, schemas, datasourceId, onChange }) {
  const [adding, setAdding] = useState(false)
  const [newJoin, setNewJoin] = useState({ table: '', from_col: '', to_col: '', columns: [], type: 'LEFT' })

  const schema = datasourceId ? schemas[datasourceId] : null
  const tables = schema?.tables || []
  const joinTable = tables.find(t => t.name === newJoin.table)

  const addJoin = () => {
    if (!newJoin.table || (!newJoin.from_col && !newJoin.join_expression)) return
    onChange([...joins, { ...newJoin }])
    setNewJoin({ table: '', from_col: '', to_col: '', columns: [], type: 'LEFT' })
    setAdding(false)
  }

  // Quark pattern otomatik algilama
  const autoDetectJoin = (targetTable) => {
    // BLCARI -> CARIKART.CARIKOD pattern
    if (targetTable === 'CARIKART') {
      return {
        join_expression: `CARIKART.CARIKOD = 'CR' || LPAD(${mainTable}.BLCARI, 7, '0')`,
        from_col: 'BLCARI', to_col: 'CARIKOD'
      }
    }
    if (targetTable === 'STOKKART') {
      return {
        join_expression: `STOKKART.STOKKOD = 'ST' || LPAD(${mainTable}.BLSTOK, 7, '0')`,
        from_col: 'BLSTOK', to_col: 'STOKKOD'
      }
    }
    return null
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-500 uppercase">Tablo Birlestir (JOIN)</span>
        <button onClick={() => setAdding(!adding)} className="text-primary-600">
          {adding ? <FiX size={11} /> : <FiPlus size={11} />}
        </button>
      </div>

      {joins.map((j, i) => (
        <div key={i} className="flex items-center gap-1 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 mb-1 text-[10px] text-indigo-700">
          <span className="flex-1 truncate">{j.table} ({(j.columns || []).join(', ')})</span>
          <button onClick={() => onChange(joins.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600"><FiX size={10} /></button>
        </div>
      ))}

      {joins.length === 0 && !adding && (
        <p className="text-[9px] text-gray-300 italic">Baska tablodan kolon almak icin + ile ekle</p>
      )}

      {adding && (
        <div className="bg-gray-50 border rounded-lg p-2 mt-1 space-y-1.5">
          <div>
            <label className="text-[9px] text-gray-500">Birlestirilecek Tablo</label>
            <select value={newJoin.table} onChange={(e) => {
              const tbl = e.target.value
              const auto = autoDetectJoin(tbl)
              if (auto) {
                setNewJoin({ ...newJoin, table: tbl, ...auto, columns: [] })
              } else {
                setNewJoin({ ...newJoin, table: tbl, join_expression: '', from_col: '', to_col: '', columns: [] })
              }
            }} className="w-full text-[10px] border rounded px-1.5 py-1">
              <option value="">Tablo sec...</option>
              {tables.filter(t => t.name !== mainTable).map(t => (
                <option key={t.id} value={t.name}>{t.name}</option>
              ))}
            </select>
          </div>

          {newJoin.table && newJoin.join_expression && (
            <div className="text-[9px] text-green-600 bg-green-50 rounded px-1.5 py-0.5">
              Otomatik: {newJoin.from_col} → {newJoin.to_col}
            </div>
          )}

          {newJoin.table && !newJoin.join_expression && (
            <div className="grid grid-cols-2 gap-1">
              <div>
                <label className="text-[9px] text-gray-500">{mainTable} kolonu</label>
                <select value={newJoin.from_col} onChange={(e) => setNewJoin({ ...newJoin, from_col: e.target.value })}
                  className="w-full text-[10px] border rounded px-1.5 py-1">
                  <option value="">Sec...</option>
                  {tables.find(t => t.name === mainTable)?.columns?.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] text-gray-500">{newJoin.table} kolonu</label>
                <select value={newJoin.to_col} onChange={(e) => setNewJoin({ ...newJoin, to_col: e.target.value })}
                  className="w-full text-[10px] border rounded px-1.5 py-1">
                  <option value="">Sec...</option>
                  {joinTable?.columns?.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {newJoin.table && joinTable && (
            <div>
              <label className="text-[9px] text-gray-500">Alinacak Kolonlar</label>
              <div className="max-h-28 overflow-y-auto border rounded bg-white">
                {joinTable.columns.slice(0, 30).map(col => (
                  <label key={col.id} className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] cursor-pointer hover:bg-blue-50">
                    <input type="checkbox"
                      checked={(newJoin.columns || []).includes(col.name)}
                      onChange={(e) => {
                        const cols = [...(newJoin.columns || [])]
                        if (e.target.checked) cols.push(col.name)
                        else cols.splice(cols.indexOf(col.name), 1)
                        setNewJoin({ ...newJoin, columns: cols })
                      }}
                      className="w-3 h-3 rounded border-gray-300 text-primary-600" />
                    {col.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {newJoin.table && (
            <div className="flex gap-1">
              <button onClick={addJoin} className="text-[9px] bg-primary-600 text-white px-2 py-0.5 rounded flex-1">Ekle</button>
              <button onClick={() => setAdding(false)} className="text-[9px] text-gray-500 px-2 py-0.5">Iptal</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}


function CalcColumnEditor({ calcColumns, onChange }) {
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [expression, setExpression] = useState('')

  const presets = [
    { label: 'Bakiye (Borc-Alacak)', name: 'BAKIYE', expr: 'COALESCE(SUM(BORC),0) - COALESCE(SUM(ALACAK),0)' },
    { label: 'Kar (Satis-Alis)', name: 'KAR', expr: 'COALESCE(SUM(CIKAN_TOPLAM),0) - COALESCE(SUM(GIREN_TOPLAM),0)' },
    { label: 'Kar Marji %', name: 'KAR_MARJI', expr: 'CASE WHEN SUM(CIKAN_TOPLAM)>0 THEN (SUM(CIKAN_TOPLAM)-SUM(GIREN_TOPLAM))*100/SUM(CIKAN_TOPLAM) ELSE 0 END' },
  ]

  const addColumn = () => {
    if (!name || !expression) return
    onChange([...calcColumns, { name, expression }])
    setName('')
    setExpression('')
    setAdding(false)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-500 uppercase">Hesaplanan Kolonlar</span>
        <button onClick={() => setAdding(!adding)} className="text-primary-600">
          {adding ? <FiX size={11} /> : <FiPlus size={11} />}
        </button>
      </div>

      {calcColumns.map((cc, i) => (
        <div key={i} className="flex items-center gap-1 bg-teal-50 border border-teal-200 rounded px-2 py-0.5 mb-1 text-[10px] text-teal-700">
          <span className="flex-1 truncate">{cc.name}</span>
          <button onClick={() => onChange(calcColumns.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600"><FiX size={10} /></button>
        </div>
      ))}

      {calcColumns.length === 0 && !adding && (
        <p className="text-[9px] text-gray-300 italic">+ ile hesaplanan kolon ekle</p>
      )}

      {adding && (
        <div className="bg-gray-50 border rounded-lg p-2 mt-1 space-y-1.5">
          <div>
            <label className="text-[9px] text-gray-500">Hazir Sablonlar</label>
            <div className="space-y-0.5">
              {presets.map(p => (
                <button key={p.name} onClick={() => { setName(p.name); setExpression(p.expr) }}
                  className="w-full text-left text-[9px] px-1.5 py-0.5 rounded border border-gray-200 hover:bg-teal-50 hover:border-teal-300">
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[9px] text-gray-500">Kolon Adi</label>
            <input value={name} onChange={(e) => setName(e.target.value.toUpperCase().replace(/\s/g, '_'))}
              placeholder="BAKIYE" className="w-full text-[10px] border rounded px-1.5 py-1" />
          </div>
          <div>
            <label className="text-[9px] text-gray-500">SQL Ifade</label>
            <textarea value={expression} onChange={(e) => setExpression(e.target.value)}
              placeholder="SUM(BORC) - SUM(ALACAK)" className="w-full text-[10px] border rounded px-1.5 py-1 h-12 font-mono" />
          </div>
          <div className="flex gap-1">
            <button onClick={addColumn} className="text-[9px] bg-primary-600 text-white px-2 py-0.5 rounded flex-1">Ekle</button>
            <button onClick={() => setAdding(false)} className="text-[9px] text-gray-500 px-2 py-0.5">Iptal</button>
          </div>
        </div>
      )}
    </div>
  )
}
